import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useCurrentUser } from '../current-user';
import type { PendingAction, PendingEntry } from './models';

export type PendingDraft = {
    workItemId: number;
    workItemTitle: string;
    workItemType: string;
    date: string;
    hours: number;
    action?: PendingAction;
};

type PendingContextValue = {
    pending: PendingEntry[];
    add: (draft: PendingDraft) => void;
    remove: (id: string) => void;
    updateHours: (id: string, hours: number) => void;
    clear: () => void;
    clearForDate: (date: string) => void;
    forDate: (date: string) => PendingEntry[];
    totalForDate: (date: string) => number;
};

const PendingContext = createContext<PendingContextValue | undefined>(undefined);

const EMPTY_PENDING: PendingEntry[] = [];

export const PendingProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [pending, setPending] = useState<PendingEntry[]>([]);
    const { user } = useCurrentUser();
    const userId = user?.id ?? null;

    // Queued entries belong to whoever they were queued for.
    useEffect(() => {
        setPending([]);
    }, [userId]);

    const add = useCallback((draft: PendingDraft) => {
        const entry: PendingEntry = {
            id: crypto.randomUUID(),
            workItemId: draft.workItemId,
            workItemTitle: draft.workItemTitle,
            workItemType: draft.workItemType,
            date: draft.date,
            hours: draft.hours,
            action: draft.action ?? 'add',
        };
        setPending((prev) => [...prev, entry]);
    }, []);

    const remove = useCallback((id: string) => {
        setPending((prev) => prev.filter((e) => e.id !== id));
    }, []);

    const updateHours = useCallback((id: string, hours: number) => {
        setPending((prev) => prev.map((e) => (e.id === id ? { ...e, hours } : e)));
    }, []);

    const clear = useCallback(() => {
        setPending([]);
    }, []);

    const clearForDate = useCallback((date: string) => {
        setPending((prev) => prev.filter((e) => e.date !== date));
    }, []);

    const byDate = useMemo(() => {
        const map = new Map<string, PendingEntry[]>();
        for (const entry of pending) {
            const list = map.get(entry.date);
            if (list) {
                list.push(entry);
            } else {
                map.set(entry.date, [entry]);
            }
        }
        return map;
    }, [pending]);

    const totalByDate = useMemo(() => {
        const map = new Map<string, number>();
        for (const entry of pending) {
            const delta = entry.action === 'remove' ? -entry.hours : entry.hours;
            map.set(entry.date, (map.get(entry.date) ?? 0) + delta);
        }
        return map;
    }, [pending]);

    const forDate = useCallback((date: string) => byDate.get(date) ?? EMPTY_PENDING, [byDate]);
    const totalForDate = useCallback((date: string) => totalByDate.get(date) ?? 0, [totalByDate]);

    const value = useMemo(
        () => ({ pending, add, remove, updateHours, clear, clearForDate, forDate, totalForDate }),
        [pending, add, remove, updateHours, clear, clearForDate, forDate, totalForDate],
    );

    return <PendingContext.Provider value={value}>{children}</PendingContext.Provider>;
};

export const usePending = (): PendingContextValue => {
    const ctx = useContext(PendingContext);
    if (!ctx) {
        throw new Error('usePending must be used within a PendingProvider');
    }
    return ctx;
};
